<script lang="ts">
  /**
   * L'alchimie : les Cercles, les Rituels, et ce qu'un Échec coûte.
   *
   * Le livre sépare trois choses que la table cherche ensemble — quel Cercle on
   * sait tracer, ce que le Test coûte, et ce qu'un Échec inflige. Ce panneau les
   * réunit.
   *
   * Les onze Rituels du livre sont donnés **en exemple** : il dit lui-même que
   * le meneur peut en créer d'autres. Ceux de la table sont donc ici aussi, et
   * reçoivent le Modificateur du Cercle qu'on leur donne.
   */
  import { alchimie as lireLAlchimie, type PersonnageEnregistre } from '../../../rules'

  let {
    personnage,
    ecrire,
    effacer,
  }: {
    personnage: PersonnageEnregistre
    /** Inscrit un Rituel que le meneur a écrit. */
    ecrire: (nom: string, cercle: string, duree: string | null, effet: string) => void
    effacer: (index: number) => void
  } = $props()

  const oeuvre = $derived(lireLAlchimie(personnage))
  let ouvert = $state(false)

  let nom = $state('')
  let cercle = $state('mineur')
  let duree = $state('')
  let effet = $state('')

  const signe = (v: number) => (v < 0 ? `−${Math.abs(v)}` : v === 0 ? '0' : `+${v}`)

  function inscrire() {
    if (nom.trim().length === 0) return
    ecrire(nom, cercle, duree.trim() || null, effet)
    nom = ''
    duree = ''
    effet = ''
    ouvert = false
  }
</script>

<div class="cartouche">
  <div class="bandeau">
    Alchimie
    {#if ouvert}
      <button type="button" class="refermer" onclick={() => (ouvert = false)}>Refermer</button>
    {/if}
  </div>
  <div class="corps">
    <p class="regle">
      {oeuvre.conditions}
      {#if oeuvre.atelier}
        <span class="exigence">{oeuvre.atelier} — {signe(oeuvre.bonus_d_atelier)} au Test.</span>
      {/if}
    </p>

    <ul class="plates cercles">
      {#each oeuvre.cercles as c (c.id)}
        <li class:hors={!c.ouvert}>
          <span class="quoi">
            <span class="nom">
              {c.nom}
              <span class="data">{signe(c.modificateur)}</span>
            </span>
            {#each c.sur_echec as e (e)}
              <span class="detail rate">En Échec : {e}</span>
            {/each}
            {#if c.manque}<span class="manque">il manque {c.manque}</span>{/if}
          </span>
          <span class="voie">p. {c.page}</span>
        </li>
      {/each}
    </ul>

    <ul class="achats larges">
      {#each oeuvre.rituels as r (r.id)}
        <li class:hors={!r.ouvert}>
          <span class="nom">
            {r.nom}
            <span class="exigence">
              {r.cercle_nom}
              {#if r.duree}· {r.duree}{/if}
              {#if r.page}· p. {r.page}{:else}· écrit à la table{/if}
            </span>
            <span class="exigence">{r.resume}</span>
          </span>
          <span class="chiffres data" title="Le second s'applique en enchaînant">
            {signe(r.modificateur)} · {signe(r.modificateur_enchaine)}
          </span>
          {#if r.index !== null}
            <button type="button" class="pilule discret" onclick={() => effacer(r.index ?? 0)}>
              Effacer
            </button>
          {:else}
            <span></span>
          {/if}
        </li>
      {/each}
    </ul>

    {#if oeuvre.marque_ouverte}
      <h3>Par la Marque</h3>
      <p class="regle">
        Le tatouage se passe de Cercle : le Rituel part par simple contact.
      </p>
      <ul class="achats larges">
        {#each oeuvre.marque as m (m.id)}
          <li>
            <span class="nom">
              {m.nom}
              <span class="exigence">{m.effet}</span>
            </span>
            <span class="chiffres data">{m.cout ?? 'hors Confrontation'}</span>
            <span></span>
          </li>
        {/each}
      </ul>
    {/if}

    {#if !ouvert}
      <button type="button" class="lien" onclick={() => (ouvert = true)}>
        Écrire un Rituel avec le meneur
      </button>
    {:else}
      <p class="regle">
        Le livre donne ses onze Rituels en exemple et dit que le meneur peut en
        créer d'autres. Celui que vous écrivez ici reçoit le Modificateur du
        Cercle que vous lui donnez, comme les autres.
      </p>
      <div class="atelier">
        <label class="champ large">
          Nom
          <input class="saisie" type="text" placeholder="Appel du Vif-argent" bind:value={nom} />
        </label>
        <label class="champ">
          Cercle
          <select class="saisie" bind:value={cercle}>
            {#each oeuvre.cercles as c (c.id)}
              <option value={c.id}>{c.nom}</option>
            {/each}
          </select>
        </label>
        <label class="champ">
          Durée
          <input class="saisie court" type="text" placeholder="10 minutes" bind:value={duree} />
        </label>
        <label class="champ large">
          Effet
          <input
            class="saisie"
            type="text"
            placeholder="Ce que le Rituel produit"
            bind:value={effet}
          />
        </label>
        <div class="gestes">
          <button
            type="button"
            class="pilule"
            disabled={nom.trim().length === 0}
            onclick={inscrire}
          >
            Inscrire
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  h3 {
    margin: var(--pas) 0 0;
    font-family: var(--titre);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.65rem;
    color: var(--gold);
  }
  /* Les trois Cercles : ce qu'ils coûtent, ce qu'ils font payer en Échec. */
  .cercles li {
    align-items: flex-start;
    justify-content: space-between;
    padding-bottom: calc(var(--pas) / 2);
    border-bottom: 1px solid var(--line);
  }
  .cercles .quoi {
    display: grid;
    gap: 2px;
  }
  .cercles .nom {
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    font-family: var(--titre);
  }
  .cercles li.hors {
    opacity: 0.6;
  }
  /* Ce qu'un Échec inflige : de la même couleur que ce qui manque. */
  .rate {
    color: var(--ko);
  }
  /* L'établi du meneur. */
  .atelier {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: var(--pas);
    align-items: end;
  }
  .atelier .champ {
    display: grid;
    gap: 2px;
    font-size: 0.68rem;
    font-family: var(--titre);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pale);
  }
  .atelier .champ.large,
  .atelier .gestes {
    grid-column: 1 / -1;
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
</style>
